import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import {
  Button,
  TextField,
  MenuItem,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from "@material-ui/core";
import { useTheme, alpha, makeStyles } from "@material-ui/core/styles";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";

import MainContainer from "../../components/MainContainer";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import {
  useCampaignStyles,
  PACE_PRESETS,
  DEFAULT_PACE,
  estimateSeconds,
  formatDuration,
  parseCampaignSettings,
  toneColor,
} from "../Campaigns/campaignShared";

const INTERVALS = [0, 5, 10, 15, 20, 30, 40, 60];
const EVERY = [0, 10, 20, 30, 50, 100];
const PAUSES = [0, 30, 60, 120, 180, 300];
const BUILTIN = ["nome", "numero", "email"];
const SAMPLE_SIZE = 500;

const useLocalStyles = makeStyles((theme) => ({
  grid3: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
  },
  preset: {
    textAlign: "left",
    padding: theme.spacing(1.5, 1.75),
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    font: "inherit",
    cursor: "pointer",
  },
  presetActive: {
    borderColor: theme.palette.primary.main,
    boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  },
  section: { flex: "999 1 520px", minWidth: 0 },
  sectionHead: { padding: theme.spacing(2, 2.5), borderBottom: `1px solid ${theme.palette.divider}` },
  sectionBody: { padding: theme.spacing(2.5), display: "flex", flexDirection: "column", gap: theme.spacing(2.5) },
  varRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  code: {
    fontSize: 13,
    padding: "2px 8px",
    borderRadius: 6,
    fontFamily: "monospace",
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "#f4f2ec",
  },
}));

const withCurrent = (options, current) =>
  options.includes(Number(current)) ? options : [...options, Number(current)].sort((a, b) => a - b);

const CampaignsConfig = () => {
  const classes = useCampaignStyles();
  const local = useLocalStyles();
  const theme = useTheme();

  const [settings, setSettings] = useState({ ...DEFAULT_PACE, variables: [] });
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);
  const [variable, setVariable] = useState({ key: "", value: "" });
  const [termOpen, setTermOpen] = useState(false);

  useEffect(() => {
    api
      .get("/campaign-settings")
      .then(({ data }) => {
        const s = parseCampaignSettings(data);
        const clean = {
          messageInterval: Number(s.messageInterval),
          longerIntervalAfter: Number(s.longerIntervalAfter),
          greaterInterval: Number(s.greaterInterval),
          variables: s.variables,
        };
        setSettings(clean);
        setSaved(JSON.stringify(clean));
      })
      .catch(toastError);
  }, []);

  const dirty = saved !== null && JSON.stringify(settings) !== saved;
  const set = (patch) => setSettings((s) => ({ ...s, ...patch }));

  const activePreset = PACE_PRESETS.find(
    (p) =>
      p.messageInterval === settings.messageInterval &&
      p.longerIntervalAfter === settings.longerIntervalAfter &&
      p.greaterInterval === settings.greaterInterval
  );

  const total = estimateSeconds(SAMPLE_SIZE, settings);
  const perHour = total > 0 ? Math.round(((SAMPLE_SIZE - 1) / total) * 3600) : null;

  const keyClean = variable.key.trim().replace(/[{}\s]/g, "");
  const keyError = !keyClean
    ? ""
    : !/^[A-Za-z0-9_]+$/.test(keyClean)
    ? "Use só letras sem acento, números e _"
    : BUILTIN.includes(keyClean.toLowerCase())
    ? "Esse nome já é usado pelo sistema"
    : settings.variables.some((v) => v.key === keyClean)
    ? "Já existe uma variável com esse nome"
    : "";

  const addVariable = (e) => {
    if (e) e.preventDefault();
    if (!keyClean || keyError || !variable.value.trim()) return;
    set({ variables: [...settings.variables, { key: keyClean, value: variable.value.trim() }] });
    setVariable({ key: "", value: "" });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/campaign-settings", { settings });
      setSaved(JSON.stringify(settings));
      toast.success("Configurações salvas");
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  let acceptedAt = null;
  try {
    const v = localStorage.getItem("campaignDisclaimerAccepted");
    if (v && v !== "true" && moment(v).isValid()) acceptedAt = moment(v).format("DD/MM/YYYY");
    else if (v) acceptedAt = "neste navegador";
  } catch (e) {
    // ignora
  }

  const select = (label, value, options, fmt, key) => (
    <TextField select label={label} variant="outlined" fullWidth value={value} onChange={(e) => set({ [key]: Number(e.target.value) })}>
      {withCurrent(options, value).map((o) => (
        <MenuItem key={o} value={o}>
          {fmt(o)}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <MainContainer>
      <Dialog open={termOpen} onClose={() => setTermOpen(false)}>
        <DialogTitle>{i18n.t("campaignsMessageAlert.disclaimer.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {i18n.t("campaignsMessageAlert.disclaimer.message")}
            <br />
            <br />
            <strong>{i18n.t("campaignsMessageAlert.disclaimer.responsibility")}</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={() => setTermOpen(false)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <div className={classes.root} style={{ overflowY: "auto" }}>
        <div className={classes.header}>
          <div className={classes.headerTitle}>
            <Title>Configurações de campanhas</Title>
            <Typography variant="body2" className={classes.muted}>
              Valem para todas as campanhas da empresa.
            </Typography>
          </div>
          <div className={classes.headerButtons} style={{ alignItems: "center" }}>
            {dirty && (
              <Typography variant="body2" style={{ color: toneColor(theme, "amber") }}>
                Alterações não salvas
              </Typography>
            )}
            <div style={{ position: "relative" }}>
              <Button
                variant="contained"
                color="primary"
                disableElevation
                className={classes.headerButton}
                disabled={!dirty || saving}
                onClick={save}
              >
                Salvar
              </Button>
              {saving && <CircularProgress size={22} style={{ position: "absolute", top: "50%", left: "50%", marginTop: -11, marginLeft: -11 }} />}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start" }}>
          <div className={`${classes.card} ${local.section}`}>
            <div className={local.sectionHead}>
              <div className={classes.cardTitle}>Ritmo de envio</div>
              <Typography variant="body2" className={classes.muted}>
                Enviar devagar parece mais natural e reduz o risco de o número ser bloqueado pelo WhatsApp.
              </Typography>
            </div>
            <div className={local.sectionBody}>
              <div className={local.grid3} role="group" aria-label="Ritmos prontos">
                {PACE_PRESETS.map((p) => {
                  const active = activePreset?.key === p.key;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      aria-pressed={active}
                      className={`${local.preset} ${active ? local.presetActive : ""}`}
                      onClick={() =>
                        set({ messageInterval: p.messageInterval, longerIntervalAfter: p.longerIntervalAfter, greaterInterval: p.greaterInterval })
                      }
                    >
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{p.label}</div>
                      <div className={classes.subtle} style={{ marginTop: 2 }}>
                        1 a cada {p.messageInterval} s · pausa de {formatDuration(p.greaterInterval)} a cada {p.longerIntervalAfter}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className={local.grid3}>
                {select("Intervalo entre mensagens", settings.messageInterval, INTERVALS, (o) => (o ? `${o} segundos` : "Sem intervalo"), "messageInterval")}
                {select("Pausa maior a cada", settings.longerIntervalAfter, EVERY, (o) => (o ? `${o} mensagens` : "Nunca"), "longerIntervalAfter")}
                {select("Duração da pausa", settings.greaterInterval, PAUSES, (o) => (o ? formatDuration(o) : "Sem pausa"), "greaterInterval")}
              </div>

              <div className={classes.callout} style={{ margin: 0 }}>
                <b>Na prática:</b>{" "}
                {total > 0 ? (
                  <>
                    uma lista de {SAMPLE_SIZE} contatos leva cerca de <b>{formatDuration(total)}</b> para terminar
                    {perHour ? ` (≈ ${perHour} mensagens por hora)` : ""}.
                  </>
                ) : (
                  <>as mensagens saem todas quase ao mesmo tempo — não recomendado.</>
                )}
                {settings.messageInterval > 0 && settings.messageInterval < 10 && (
                  <span style={{ display: "block", marginTop: 4, color: toneColor(theme, "amber") }}>
                    Intervalos curtos aumentam a chance de bloqueio do número.
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className={classes.card} style={{ flex: "1 1 360px", minWidth: 0 }}>
            <div className={local.sectionHead}>
              <div className={classes.cardTitle}>Variáveis personalizadas</div>
              <Typography variant="body2" className={classes.muted}>
                Textos fixos que você reaproveita nas mensagens, como um cupom ou link.
              </Typography>
            </div>
            <div className={local.sectionBody} style={{ gap: 12 }}>
              <Typography variant="body2" className={classes.muted} style={{ fontSize: 12 }}>
                Já disponíveis em toda campanha: <b>{"{nome}"}</b> <b>{"{numero}"}</b> <b>{"{email}"}</b>
              </Typography>
              <div>
                {settings.variables.length === 0 && (
                  <Typography variant="body2" className={classes.muted}>
                    Nenhuma variável ainda.
                  </Typography>
                )}
                {settings.variables.map((v) => (
                  <div key={v.key} className={local.varRow}>
                    <code className={local.code}>{`{${v.key}}`}</code>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={v.value}>
                      {v.value}
                    </span>
                    <IconButton
                      size="small"
                      aria-label={`Excluir variável ${v.key}`}
                      className={classes.deleteAction}
                      onClick={() => set({ variables: settings.variables.filter((x) => x.key !== v.key) })}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </div>
                ))}
              </div>
              <form onSubmit={addVariable} style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "flex-start" }}>
                <TextField
                  label="Nome"
                  size="small"
                  variant="outlined"
                  value={variable.key}
                  onChange={(e) => setVariable({ ...variable, key: e.target.value })}
                  error={!!keyError}
                  helperText={keyError || "Ex.: cupom"}
                  style={{ flex: "1 1 110px" }}
                />
                <TextField
                  label="Conteúdo"
                  size="small"
                  variant="outlined"
                  value={variable.value}
                  onChange={(e) => setVariable({ ...variable, value: e.target.value })}
                  helperText="Ex.: BLACK30"
                  style={{ flex: "2 1 160px" }}
                />
                <Button
                  type="submit"
                  className={`${classes.rowButton} ${classes.tintBlue}`}
                  style={{ height: 40 }}
                  disabled={!keyClean || !!keyError || !variable.value.trim()}
                >
                  Adicionar
                </Button>
              </form>
              {dirty && (
                <Typography variant="caption" style={{ color: toneColor(theme, "amber") }}>
                  Clique em Salvar no topo para gravar.
                </Typography>
              )}
            </div>
          </div>
        </div>

        <div className={classes.card} style={{ marginTop: 16, padding: theme.spacing(2, 2.5), display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div className={classes.cardTitle}>Termo de uso de campanhas</div>
            <Typography variant="body2" className={classes.muted}>
              {acceptedAt ? `Aceito ${acceptedAt.includes("/") ? `em ${acceptedAt}` : acceptedAt}.` : "Ainda não aceito neste navegador."} Fica aqui
              para consulta a qualquer momento.
            </Typography>
          </div>
          <Button variant="outlined" className={`${classes.rowButton} ${classes.rowButtonOutlined}`} style={{ height: 36 }} onClick={() => setTermOpen(true)}>
            Ler o termo
          </Button>
        </div>
      </div>
    </MainContainer>
  );
};

export default CampaignsConfig;
